import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { apiFetch } from './api.js';
import { type ApiBrowseSummary, BROWSE_PAGE_SIZE, firstServerSort } from './browse-query.js';
import type { FieldEvidenceMap } from './field-evidence.js';
import { type FilterParamEntries, appendFilterEntries, filterEntriesKey } from './filter-params.js';
import type { AccountKind, AccountRow } from './mock-accounts.js';
import type { ApiProjectAccountRole, ApiProjectSource } from './projects.js';
import type { RecordScores } from './scores.js';

const PAGE_SIZE = 1000;
const AUTO_PAGE_CAP = 2000;
// Keep an already-loaded list "fresh" for 5 min so returning to the page serves
// cached rows instantly instead of re-streaming every page. List-mutating
// actions invalidate ['accounts'] explicitly, which overrides this.
const LIST_STALE_TIME_MS = 5 * 60_000;

// -- Wire types (mirror the API's OpenAPI schema) ----------------------------

export type ApiAccountType =
  | 'developer'
  | 'architect'
  | 'gc'
  | 'engineer'
  | 'agency'
  | 'law_firm'
  | 'consultant'
  | 'landscape_architect'
  | 'other';

/** The firm's size, industry, age and revenue as Parcel's people search filed them. */
export interface ApiAccountFirmographics {
  headcount: number | null;
  headcount_range: string | null;
  headcount_growth_1y: number | null;
  industry: string | null;
  company_type: string | null;
  founded_year: number | null;
  revenue_min: number | null;
  revenue_max: number | null;
  revenue_currency: string | null;
}

/** "2,069", else the provider's band ("1,001–5,000"); null when neither is known. */
export function formatHeadcount(f: ApiAccountFirmographics | null | undefined): string | null {
  if (f?.headcount != null) return f.headcount.toLocaleString('en-US');
  const band = f?.headcount_range?.match(/^(\d+)\s*-\s*(\d+)$/);
  if (band)
    return `${Number(band[1]).toLocaleString('en-US')}–${Number(band[2]).toLocaleString('en-US')}`;
  return f?.headcount_range ?? null;
}

const COMPACT_MONEY = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** "$10M–$50M", one end alone when only one is known. */
export function formatRevenue(f: ApiAccountFirmographics | null | undefined): string | null {
  if (f?.revenue_min == null && f?.revenue_max == null) return null;
  const sign =
    !f?.revenue_currency || f.revenue_currency === 'USD' ? '$' : `${f.revenue_currency} `;
  const one = (value: number) => `${sign}${COMPACT_MONEY.format(value)}`;
  if (f.revenue_min != null && f.revenue_max != null) {
    return `${one(f.revenue_min)}–${one(f.revenue_max)}`;
  }
  return f.revenue_min != null ? `${one(f.revenue_min)}+` : `Up to ${one(f.revenue_max as number)}`;
}

export interface ApiAccountSummary {
  id: string;
  name: string;
  account_types: ApiAccountType[];
  aliases: string[];
  domains: string[];
  website: string | null;
  linkedin_url: string | null;
  city: string | null;
  state: string | null;
  /** This workspace's ICP Score; an older API omits it. */
  scores?: RecordScores;
  projects_count: number;
  last_activity_at: string | null;
  created_at: string;
  /** On list and detail reads; an older API omits it. */
  firmographics?: ApiAccountFirmographics;
}

export interface ApiAccountDetail extends ApiAccountSummary {
  parent_account_id: string | null;
  signals_count: number;
  /** The organization profile (PR11); absent from an API that predates it. */
  hq_address?: string | null;
  hq_location?: { lat: number; lng: number } | null;
  public_phone?: string | null;
  public_email?: string | null;
  specialties?: string[];
  description?: string | null;
  founded_year?: number | null;
  account_types_source?: 'resolved' | 'enriched';
  /** Where each profile value came from, keyed by field; a value with no entry has no cited source. */
  field_evidence?: FieldEvidenceMap;
}

/** Totals over the account's projects: each known_* sum covers known_*_projects of total_projects. */
export interface ApiAccountPortfolio {
  total_projects: number;
  active_projects: number;
  known_sf: number;
  known_sf_projects: number;
  known_cost_usd: number;
  known_cost_projects: number;
  known_units: number;
  known_units_projects: number;
  stage_mix: Array<{ stage: ApiLinkedProject['stage']; projects: number }>;
  area_of_work: Array<{ city: string; state: string; projects: number }>;
  sectors: Array<{ primary_use: string | null; projects: number }>;
  roles_played: Array<{ role: string; projects: number }>;
}

export interface ApiLinkedProject {
  id: string;
  name: string | null;
  primary_address: string;
  city: string;
  state: string;
  stage:
    | 'pre_filing'
    | 'filed'
    | 'under_review'
    | 'approved'
    | 'permitted'
    | 'under_construction'
    | 'completed'
    | null;
  disposition: 'active' | 'denied' | 'withdrawn' | 'expired' | 'stalled' | 'cancelled';
  // Array because an account can hold multiple roles on the same project
  // (developer + owner, etc.). Always non-empty for linked projects.
  roles: ApiProjectAccountRole[];
  last_signal_at: string | null;
  latest_source: ApiProjectSource | null;
}

export interface ApiAccountSignal {
  id: string;
  project_id: string;
  project_name: string | null;
  project_primary_address: string;
  signal_type: string;
  filed_at: string;
  discovered_at: string;
  source_url: string | null;
  source_filing_id: string | null;
  source_id: string;
  source_slug: string;
  source_display_name: string;
  source_base_url: string;
  source_logo_domain: string | null;
  reported_cost_usd: number | null;
  reported_gross_floor_area_sf: number | null;
}

export interface ApiAccountListResponse {
  accounts: ApiAccountSummary[];
  pagination: {
    limit: number;
    offset: number;
    total: number | null;
    next_cursor?: string | null;
    has_more?: boolean;
    as_of?: string;
  };
}

export interface ApiAccountDetailResponse {
  account: ApiAccountDetail;
}

export interface ApiAccountProjectsResponse {
  projects: ApiLinkedProject[];
  pagination: { limit: number; offset: number; total: number | null };
}

// -- Mapping -----------------------------------------------------------------

const KIND_MAP: Record<ApiAccountType, AccountKind> = {
  developer: 'developer',
  architect: 'architect',
  engineer: 'engineer',
  gc: 'contractor',
  agency: 'agency',
  law_firm: 'law_firm',
  consultant: 'consultant',
  landscape_architect: 'landscape_architect',
  other: 'unknown',
};

// Order used ONLY to pick an icon/kind when an account has multiple types.
// Not an identity precedence — the full set is what's rendered.
const TYPE_DISPLAY_ORDER: ApiAccountType[] = [
  'developer',
  'gc',
  'architect',
  'landscape_architect',
  'engineer',
  'consultant',
  'law_firm',
  'agency',
];

export function primaryAccountType(types: ApiAccountType[]): ApiAccountType | null {
  for (const t of TYPE_DISPLAY_ORDER) if (types.includes(t)) return t;
  return null;
}

export function apiAccountToRow(a: ApiAccountSummary): AccountRow {
  const primary = primaryAccountType(a.account_types);

  return {
    id: a.id,
    name: a.name,
    kind: primary ? KIND_MAP[primary] : 'unknown',
    account_types: a.account_types,
    website: a.website,
    domains: a.domains,
    linkedinUrl: a.linkedin_url ?? null,
    firmographics: a.firmographics ?? null,
    city: a.city ?? '—',
    state: a.state ?? '—',
    projectsCount: a.projects_count,
    contactsCount: 0,
    scores: a.scores ?? {},
    lastActivityAt: a.last_activity_at,
    createdAt: a.created_at,
  };
}

// -- Hooks -------------------------------------------------------------------

function buildListQuery(params: {
  workspaceId: string;
  limit: number;
  offset: number;
  filter?: FilterParamEntries;
  viewId?: string;
}): string {
  const qs = new URLSearchParams({
    workspace_id: params.workspaceId,
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.viewId) qs.set('view_id', params.viewId);
  appendFilterEntries(qs, params.filter);
  return qs.toString();
}

function useAutoFetchNextPage(
  enabled: boolean,
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
  fetchNextPage: () => Promise<unknown>,
  isPlaceholderData = false,
) {
  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage && !isPlaceholderData) {
      void fetchNextPage();
    }
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage, isPlaceholderData]);
}

function useLegacyWorkspaceAccounts(
  workspaceId: string | undefined,
  filter?: FilterParamEntries,
  staticViewId?: string,
  options?: { enabled?: boolean },
) {
  const enabled = Boolean(workspaceId) && (options?.enabled ?? true);
  const infinite = useInfiniteQuery({
    queryKey: ['accounts', workspaceId, filterEntriesKey(filter), staticViewId ?? null],
    enabled,
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    staleTime: LIST_STALE_TIME_MS,
    queryFn: async ({ pageParam }) => {
      const qs = buildListQuery({
        workspaceId: workspaceId ?? '',
        limit: PAGE_SIZE,
        offset: pageParam,
        filter,
        viewId: staticViewId,
      });
      return apiFetch<ApiAccountListResponse>(`/accounts?${qs}`);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.accounts.length, 0);
      if (lastPage.accounts.length < PAGE_SIZE) return undefined;
      if (loaded >= AUTO_PAGE_CAP) return undefined;
      return loaded;
    },
  });

  useAutoFetchNextPage(
    enabled,
    infinite.hasNextPage,
    infinite.isFetchingNextPage,
    infinite.fetchNextPage,
    infinite.isPlaceholderData,
  );

  const accounts = useMemo(
    () => (infinite.data?.pages ?? []).flatMap((page) => page.accounts),
    [infinite.data?.pages],
  );
  const total = infinite.data?.pages[0]?.pagination.total ?? null;
  const data = useMemo(
    () => ({
      accounts,
      pagination: { limit: PAGE_SIZE, offset: 0, total },
    }),
    [accounts, total],
  );

  return useMemo(
    () => ({
      data,
      isLoading: infinite.isLoading,
      isPlaceholderData: infinite.isPlaceholderData,
      isError: infinite.isError,
      isFetching: infinite.isFetching || infinite.isFetchingNextPage,
      fetchNextPage: infinite.fetchNextPage,
      hasNextPage: infinite.hasNextPage,
      isFetchingNextPage: infinite.isFetchingNextPage,
      isFetchNextPageError: infinite.isFetchNextPageError,
    }),
    [
      data,
      infinite.isError,
      infinite.isFetching,
      infinite.isFetchingNextPage,
      infinite.isFetchNextPageError,
      infinite.fetchNextPage,
      infinite.hasNextPage,
      infinite.isLoading,
      infinite.isPlaceholderData,
    ],
  );
}

const ACCOUNT_BROWSE_SORTS = ['name', 'icpScore', 'lastActivityAt'] as const;
const ACCOUNT_BROWSE_FALLBACK = { id: 'lastActivityAt', desc: true };

function useCursorWorkspaceAccounts(
  workspaceId: string | undefined,
  filter: FilterParamEntries | undefined,
  staticViewId: string | undefined,
  sorting: SortingState,
  enabled: boolean,
) {
  const { sort, direction } = firstServerSort(
    sorting,
    ACCOUNT_BROWSE_FALLBACK,
    ACCOUNT_BROWSE_SORTS,
  );
  const infinite = useInfiniteQuery({
    queryKey: [
      'accounts-browse',
      workspaceId,
      filterEntriesKey(filter),
      staticViewId ?? null,
      sort,
      direction,
    ],
    enabled: Boolean(workspaceId) && enabled,
    initialPageParam: null as string | null,
    staleTime: LIST_STALE_TIME_MS,
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: String(BROWSE_PAGE_SIZE),
        sort,
        direction,
      });
      if (pageParam) qs.set('cursor', pageParam);
      if (staticViewId) qs.set('view_id', staticViewId);
      appendFilterEntries(qs, filter);
      return apiFetch<ApiAccountListResponse>(`/accounts?${qs.toString()}`);
    },
    getNextPageParam: (lastPage) => lastPage.pagination.next_cursor ?? undefined,
  });

  const accounts = useMemo(
    () => (infinite.data?.pages ?? []).flatMap((page) => page.accounts),
    [infinite.data?.pages],
  );
  const data = useMemo(
    () => ({
      accounts,
      pagination: infinite.data?.pages[0]?.pagination ?? {
        limit: BROWSE_PAGE_SIZE,
        offset: 0,
        total: null,
      },
    }),
    [accounts, infinite.data?.pages],
  );

  return useMemo(
    () => ({
      data,
      isLoading: infinite.isLoading,
      isPlaceholderData: false,
      isError: infinite.isError,
      isFetching: infinite.isFetching,
      fetchNextPage: infinite.fetchNextPage,
      hasNextPage: infinite.hasNextPage,
      isFetchingNextPage: infinite.isFetchingNextPage,
      isFetchNextPageError: infinite.isFetchNextPageError,
    }),
    [
      data,
      infinite.fetchNextPage,
      infinite.hasNextPage,
      infinite.isError,
      infinite.isFetchNextPageError,
      infinite.isFetching,
      infinite.isFetchingNextPage,
      infinite.isLoading,
    ],
  );
}

export interface WorkspaceAccountsOptions {
  enabled?: boolean;
  browseMode?: boolean;
  sorting?: SortingState;
}

export function useWorkspaceAccounts(
  workspaceId: string | undefined,
  filter?: FilterParamEntries,
  staticViewId?: string,
  options?: WorkspaceAccountsOptions,
) {
  const enabled = options?.enabled ?? true;
  const browseMode = options?.browseMode ?? false;
  const legacy = useLegacyWorkspaceAccounts(workspaceId, filter, staticViewId, {
    enabled: enabled && !browseMode,
  });
  const cursor = useCursorWorkspaceAccounts(
    workspaceId,
    filter,
    staticViewId,
    options?.sorting ?? [],
    enabled && browseMode,
  );

  return browseMode ? cursor : legacy;
}

export function useAccountBrowseSummary(
  workspaceId: string | undefined,
  filter?: FilterParamEntries,
  staticViewId?: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [
      'account-browse-summary',
      workspaceId,
      filterEntriesKey(filter),
      staticViewId ?? null,
    ],
    enabled: Boolean(workspaceId) && (options?.enabled ?? true),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    queryFn: async () => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '' });
      if (staticViewId) qs.set('view_id', staticViewId);
      appendFilterEntries(qs, filter);
      return apiFetch<ApiBrowseSummary>(`/account-browse/summary?${qs.toString()}`);
    },
  });
}

/**
 * Tiny unpaginated lookup powering record typeaheads (global search, filter
 * bar). Callers are expected to debounce `q` — every distinct trimmed value
 * past 2 chars is a new query key and fires a request.
 */
export function useAccountSearchPreview(
  workspaceId: string | undefined,
  q: string,
  scopeFilter?: FilterParamEntries,
  staticViewId?: string,
) {
  const trimmed = q.trim();
  const previewFilter = [
    ...(scopeFilter ?? []).filter(([key]) => key !== 'q'),
    ['q', trimmed] as const,
  ];
  return useQuery({
    queryKey: [
      'accounts-preview',
      workspaceId,
      filterEntriesKey(previewFilter),
      staticViewId ?? null,
    ],
    enabled: Boolean(workspaceId) && trimmed.length >= 2,
    staleTime: 10_000,
    queryFn: async () => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '', limit: '5' });
      if (staticViewId) qs.set('view_id', staticViewId);
      appendFilterEntries(qs, scopeFilter);
      qs.set('q', trimmed);
      return apiFetch<ApiAccountListResponse>(`/accounts?${qs.toString()}`);
    },
  });
}

export function useAccountDetail(accountId: string | null, workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['account-detail', accountId, workspaceId],
    enabled: Boolean(accountId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '' }).toString();
      return apiFetch<ApiAccountDetailResponse>(
        `/accounts/${encodeURIComponent(accountId ?? '')}?${qs}`,
      );
    },
  });
}

export function useAccountProjects(accountId: string | null, workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['account-projects', accountId, workspaceId],
    enabled: Boolean(accountId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: '200',
      }).toString();
      return apiFetch<ApiAccountProjectsResponse>(
        `/accounts/${encodeURIComponent(accountId ?? '')}/projects?${qs}`,
      );
    },
  });
}

export function useAccountPortfolio(accountId: string | null, workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['account-portfolio', accountId, workspaceId],
    enabled: Boolean(accountId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '' }).toString();
      return apiFetch<{ portfolio: ApiAccountPortfolio }>(
        `/accounts/${encodeURIComponent(accountId ?? '')}/portfolio?${qs}`,
      );
    },
  });
}
